import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from 'react-use'
import BigNumber from 'bignumber.js'
import { useAtomValue } from 'jotai/react'

import { CoinContact, OrderType } from '@/constants/common'
import { Decimal } from '@/constants/trade'
import { min2Max } from '@/lib/utils'
import { services } from '@/stores'

import { ListParams, ListResult } from './types'
import useFetch, { Method, refreshInterval } from './useFetch'
import { Token } from './useTokenInfo'

export enum TokenOrderColumn {
  CreateAt = 0,
  LastTrade = 1,
  LastReply = 2,
  MarketCap = 3
}

const useTokenList = (
  orderType: OrderType = OrderType.ASC,
  orderColumn: TokenOrderColumn,
  listParams: ListParams = { pageNo: 1, pageSize: 30 }
) => {
  const solPriceUsd = useAtomValue(services.solPriceUsdAtom)
  const [searchParams] = useSearchParams()

  const [keyword, setKeyword] = useState('')

  const [total, setTotal] = useState<number>(0)
  const { data, ...rest } = useFetch<ListResult<Token>['data']>(
    listParams.pageSize ? '/token/list' : null,
    Method.POST,
    {
      ...listParams,
      orderType,
      orderColumn,
      keyword,
      showMyCreate: false,
      platType: 1
    },
    {
      onSuccess: (data) => {
        data.list?.forEach((info) => {
          info.market_cap_usd = new BigNumber(info.market_cap)
            .multipliedBy(solPriceUsd || '')
            .toFixed()
          info.trading_volume_usd = new BigNumber(
            min2Max(info.trading_volume, Decimal.SOL)
          )
            .multipliedBy(solPriceUsd || '')
            .toFixed()
          info.contacts = [
            { type: CoinContact.Website, href: info.website },
            { type: CoinContact.Twitter, href: info.twitter },
            { type: CoinContact.Telegram, href: info.telegram },
            { type: CoinContact.Discord, href: info.discord }
          ].filter(({ href }) => !!href)
        })
      }
    },
    undefined,
    {
      refreshInterval,
      dedupingInterval: 0
    }
  )

  useDebounce(
    () => {
      setKeyword(searchParams.get('q') || '')
    },
    200,
    [searchParams]
  )

  useEffect(() => {
    if (!rest.isLoading) {
      setTotal(Number(data?.total) || 0)
    }
  }, [data?.total, rest.isLoading])

  return {
    data: data?.list || [],
    total,
    ...rest,
    isLoading: !listParams.pageSize || rest.isLoading
  }
}

export default useTokenList

export const useMyTokenList = (
  orderType: OrderType = OrderType.ASC,
  orderColumn: TokenOrderColumn,
  listParams: ListParams = { pageNo: 1, pageSize: 30 }
) => {
  const solPriceUsd = useAtomValue(services.solPriceUsdAtom)
  const [total, setTotal] = useState<number>(0)
  const { data, ...rest } = useFetch<ListResult<Token>['data']>(
    '/token/list',
    Method.POST,
    {
      ...listParams,
      orderType,
      orderColumn,
      keyword: '',
      showMyCreate: true
    },
    {
      onSuccess: (data) => {
        data.list?.forEach((info) => {
          info.market_cap_usd = BigNumber(info.market_cap)
            .multipliedBy(solPriceUsd || '')
            .toFixed()
        })
      }
    },
    undefined,
    undefined
  )

  useEffect(() => {
    if (!rest.isLoading) {
      setTotal(Number(data?.total) || 0)
    }
  }, [data?.total, rest.isLoading])

  return {
    data: data?.list || [],
    // data: [],
    total,
    ...rest
  }
}

export const useCompleteTokens = () => {
  const solPriceUsd = useAtomValue(services.solPriceUsdAtom)

  const { data, ...rest } = useFetch<Token[]>(
    '/token/last/complete',
    Method.POST,
    {
      platType: 1
    },
    {
      onSuccess: (data) => {
        data?.forEach((info) => {
          info.market_cap_usd = new BigNumber(info.market_cap)
            .multipliedBy(solPriceUsd || '')
            .toFixed()
          info.trading_volume_usd = new BigNumber(
            min2Max(info.trading_volume, Decimal.SOL)
          )
            .multipliedBy(solPriceUsd || '')
            .toFixed()
          info.contacts = [
            { type: CoinContact.Website, href: info.website },
            { type: CoinContact.Twitter, href: info.twitter },
            { type: CoinContact.Telegram, href: info.telegram },
            { type: CoinContact.Discord, href: info.discord }
          ].filter(({ href }) => !!href)
        })
      }
    },
    undefined,
    {
      refreshInterval,
      dedupingInterval: 0
    }
  )

  return {
    data: data || [],
    ...rest
  }
}
